import {
  Avatar,
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo, useState } from "react";

export default function ChatSidebarGrupos({
  grupos = [],
  loading = false,
  selectedId,
  onSelect,
  search,
  setSearch,
  branchId,
  setBranchId,
  areaId,
  setAreaId,
}) {
  const [branchAnchor, setBranchAnchor] = useState(null);
  const [areaAnchor, setAreaAnchor] = useState(null);

  const branches = useMemo(() => {
    const map = new Map();

    for (const group of grupos) {
      if (!group.id_sucursal) continue;
      map.set(Number(group.id_sucursal), group.sucursal_nombre || `Sucursal #${group.id_sucursal}`);
    }

    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [grupos]);

  const areas = useMemo(() => {
    const map = new Map();

    for (const group of grupos) {
      if (!group.id_area) continue;
      map.set(Number(group.id_area), group.area_nombre || `Área #${group.id_area}`);
    }

    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [grupos]);

  const selectedBranchName =
    branches.find((item) => String(item.id) === String(branchId))?.name ?? "";

  const selectedAreaName =
    areas.find((item) => String(item.id) === String(areaId))?.name ?? "";

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar grupo..."
          size="small"
          sx={{
            flex: 1,
            minWidth: 180,
            "& .MuiOutlinedInput-root": {
              height: 32,
              borderRadius: "20px",
              bgcolor: (t) => t.palette?.neutral?.[50],
              boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
              "& fieldset": { border: "none" },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: (t) => t.palette?.primary?.[900], opacity: 0.9 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box>
          <Button
            onClick={(e) => setBranchAnchor(e.currentTarget)}
            sx={{
              height: 32,
              borderRadius: 999,
              bgcolor: (t) => t.palette?.secondary?.[500],
              color: "common.white",
              px: 1.5,
              textTransform: "none",
              fontWeight: 800,
              "&:hover": { bgcolor: (t) => t.palette?.secondary?.[600] },
            }}
            endIcon={<ExpandMoreIcon />}
          >
            {selectedBranchName || "Sucursal"}
          </Button>

          <Menu
            anchorEl={branchAnchor}
            open={Boolean(branchAnchor)}
            onClose={() => setBranchAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setBranchId("");
                setBranchAnchor(null);
              }}
            >
              Todas las sucursales
            </MenuItem>

            {branches.map((branch) => (
              <MenuItem
                key={branch.id}
                onClick={() => {
                  setBranchId(String(branch.id));
                  setBranchAnchor(null);
                }}
              >
                {branch.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Box>
          <Button
            onClick={(e) => setAreaAnchor(e.currentTarget)}
            sx={{
              height: 32,
              borderRadius: 999,
              bgcolor: (t) => t.palette?.secondary?.[500],
              color: "common.white",
              px: 1.5,
              textTransform: "none",
              fontWeight: 800,
              "&:hover": { bgcolor: (t) => t.palette?.secondary?.[600] },
            }}
            endIcon={<ExpandMoreIcon />}
          >
            {selectedAreaName || "Área"}
          </Button>

          <Menu
            anchorEl={areaAnchor}
            open={Boolean(areaAnchor)}
            onClose={() => setAreaAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                setAreaId("");
                setAreaAnchor(null);
              }}
            >
              Todas las áreas
            </MenuItem>

            {areas.map((area) => (
              <MenuItem
                key={area.id}
                onClick={() => {
                  setAreaId(String(area.id));
                  setAreaAnchor(null);
                }}
              >
                {area.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Stack>

      <Box
        sx={{
          p: 1,
          borderRadius: "20px",
          bgcolor: (t) => t.palette?.neutral?.[100],
          boxShadow: "inset 0px 0px 4px rgba(165, 165, 178, 0.9)",
          height: "50.6vh",
          overflow: "hidden",
          display: "flex",
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {loading ? (
            <Typography sx={{ p: 1, opacity: 0.75 }}>Cargando grupos…</Typography>
          ) : grupos.length === 0 ? (
            <Stack
              spacing={1}
              sx={{
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 4,
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: "primary.900" }}>
                Sin grupos
              </Typography>

              <Typography sx={{ fontSize: 14, opacity: 0.8 }}>
                No hay grupos para mostrar con los filtros actuales.
              </Typography>
            </Stack>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1,
                alignContent: "start",
              }}
            >
              {grupos.map((group) => {
                const active = String(group.id_grupo) === String(selectedId);
                const hasUnread = Boolean(!group.leido);
                const areaName = group.area_nombre || "Grupo";
                const branchName = group.sucursal_nombre || "Sucursal";

                return (
                  <Box
                    key={group.id_grupo}
                    onClick={() => onSelect?.(group.id_grupo)}
                    sx={{
                      p: 1,
                      borderRadius: "14px",
                      cursor: "pointer",
                      bgcolor: "common.white",
                      border: active
                        ? "2px solid rgba(52, 67, 111, 0.35)"
                        : "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
                      "&:hover": { bgcolor: (t) => t.palette?.neutral?.[50] },
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      minWidth: 0,
                      position: "relative",
                    }}
                  >
                    {hasUnread ? (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: "#D92D20",
                          border: "2px solid #fff",
                          boxShadow: "0 0 0 1px rgba(217,45,32,0.25)",
                        }}
                      />
                    ) : null}

                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: (t) => t.palette?.primary?.[900],
                        color: "common.white",
                        fontWeight: 900,
                        opacity: 0.95,
                        flex: "0 0 auto",
                      }}
                    >
                      {(String(areaName).trim().slice(0, 1) || "G").toUpperCase()}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 800, color: "primary.900", lineHeight: 1.4 }}
                        noWrap
                      >
                        {areaName}
                      </Typography>

                      <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }} noWrap>
                        Sucursal: <b>{branchName}</b>
                      </Typography>

                      <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }} noWrap>
                        Integrantes: <b>{group.miembros_count ?? 0}</b>
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
}